"use client";

import Link from "next/link";
import { BellIcon } from "@heroicons/react/24/outline";
import { useAuth } from "@/contexts/AuthContext";
import { useNotificationUnreadCount } from "@/lib/hooks/useNotificationUnreadCount";

type NotificationBellMode = "link" | "dropdown";

type NotificationBellProps = {
  mode?: NotificationBellMode;
  onClick?: () => void;
  ariaExpanded?: boolean;
  ariaControls?: string;
};

/**
 * Header bell: unread badge. Default `link` goes to /notifications; `dropdown`
 * toggles a panel (mobile header + large-screen header).
 */
export default function NotificationBell({
  mode = "link",
  onClick,
  ariaExpanded,
  ariaControls,
}: NotificationBellProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { unread } = useNotificationUnreadCount(
    isAuthenticated && !isLoading,
    user?.id,
  );

  const badge = unread > 0 ? (unread > 99 ? "99+" : String(unread)) : null;

  const label =
    unread > 0 ? `Notifications, ${unread} unread` : "Notifications";

  const className =
    "relative rounded-md p-2 text-text-light hover:bg-tertiary hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary";

  if (mode === "dropdown") {
    return (
      <button
        type="button"
        className={className}
        aria-label={label}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        onClick={onClick}
      >
        <BellIcon className="h-6 w-6" aria-hidden />
        {badge ? (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[1.125rem] rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-tight text-white"
            aria-hidden
          >
            {badge}
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <Link href="/notifications" className={className} aria-label={label}>
      <BellIcon className="h-6 w-6" aria-hidden />
      {badge ? (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[1.125rem] rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-tight text-white"
          aria-hidden
        >
          {badge}
        </span>
      ) : null}
    </Link>
  );
}
